/**
 * Trading-calendar day attribution.
 *
 * The calendar lets the user define a "trading day start hour" in UTC.
 * A trading session that *starts* at that hour belongs to the calendar day it
 * starts on; trades that happen *before* that hour are the tail end of the
 * previous session and therefore roll back to the previous trading day.
 *
 * Example with tradingDayStartUtcHour = 13:
 *   - 2026-06-12 14:00 UTC  -> 2026-06-12  (>= 13h, same day)
 *   - 2026-06-12 09:00 UTC  -> 2026-06-11  (< 13h, previous trading day)
 *   - 2026-06-12 13:00 UTC  -> 2026-06-12  (exactly 13h, same day)
 *
 * The returned key is a `yyyy-MM-dd` string built from the *shifted* UTC
 * calendar date, matching the UTC keys the calendar grid uses for its cells.
 */
export function tradeDayKey(date: Date | string, tradingDayStartUtcHour: number): string {
  const raw = typeof date === "string" ? new Date(date) : date;
  // Shift the timestamp back by the start-hour offset, then read the UTC date.
  // After the shift, anything that was before the start hour falls onto the
  // previous calendar day, and anything at/after it stays on its own day.
  const shifted = new Date(raw.getTime() - tradingDayStartUtcHour * 3_600_000);
  return utcDateKey(shifted);
}

/**
 * `yyyy-MM-dd` key from the UTC calendar date of a timestamp.
 *
 * Used for the calendar grid cells so that trade attribution (which is computed
 * in UTC, since the trading-day-start hour is a UTC setting) lands on the same
 * cell the user sees — independent of the browser's local timezone.
 */
export function utcDateKey(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
